import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import api from "../../../API/axiosInstance";
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import { API_ENDPOINTS } from "config/api";
import safeToast from "utils/safeToast";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const SmsInvitationManager = () => {
  const gridRef = useRef(null);
  
  // Data states
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registrationType, setRegistrationType] = useState(0); // 0=All, 1=Registered, 2=Unregistered
  
  // SMS related states
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateContent, setNewTemplateContent] = useState("");
  const [sendingSms, setSendingSms] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedPreviewStudent, setSelectedPreviewStudent] = useState(null);
  const [paramValues, setParamValues] = useState({});

  const placeholders = useMemo(() => {
    if (!selectedTemplate?.content) return [];
    // Extract all placeholders in order (including duplicates for display purposes)
    const matches = Array.from(selectedTemplate.content.matchAll(/\{(\w+)\}/g)).map((m) => (m[1] || "").toLowerCase());
    // Return unique list in order of first appearance (for input fields)
    const uniqueOrdered = [];
    const seen = new Set();
    for (const match of matches) {
      if (!seen.has(match)) {
        seen.add(match);
        uniqueOrdered.push(match);
      }
    }
    return uniqueOrdered;
  }, [selectedTemplate]);
  
  // Get all placeholders in order (including duplicates) for parameter extraction
  const allPlaceholdersInOrder = useMemo(() => {
    if (!selectedTemplate?.content) return [];
    return Array.from(selectedTemplate.content.matchAll(/\{(\w+)\}/g)).map((m) => (m[1] || "").toLowerCase());
  }, [selectedTemplate]);

  // Checkbox cell renderer for AG Grid
  const checkboxCellRenderer = useCallback((params) => {
    const student = params.data;
    const isSelected = selectedStudents.some(s => s.id === student.id);
    
    return (
      <Checkbox
        size="small"
        checked={isSelected}
        onChange={(e) => {
          e.stopPropagation();
          handleStudentSelection(student, e.target.checked);
        }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }, [selectedStudents]);

  // Preview button cell renderer
  const previewCellRenderer = useCallback((params) => {
    const student = params.data;
    
    return (
      <Button 
        size="small" 
        startIcon={<VisibilityIcon />} 
        onClick={(e) => {
          e.stopPropagation();
          setSelectedPreviewStudent(student);
        }}
      >
        Preview
      </Button>
    );
  }, []);

  // Column definitions for AG Grid
  const columnDefs = useMemo(() => [
    {
      headerName: "S.No",
      valueGetter: (params) => {
        const api = params.api;
        const rowIdx = params.node ? params.node.rowIndex : 0;
        const hasPagination = !!api.paginationGetPageSize;
        if (hasPagination) {
          const page = api.paginationGetCurrentPage?.() ?? 0;
          const pageSize = api.paginationGetPageSize?.() ?? 0;
          return pageSize ? page * pageSize + rowIdx + 1 : rowIdx + 1;
        }
        return rowIdx + 1;
      },
      width: 60,
      minWidth: 50,
      maxWidth: 70,
      resizable: false,
      sortable: false,
      filter: false,
      pinned: "left",
      suppressSizeToFit: true,
      suppressMenu: true,
      cellStyle: { textAlign: "center" },
    },
    {
      field: "fullName",
      headerName: "Name",
      sortable: true,
      filter: true,
      flex: 2,
      minWidth: 150,
    },
    {
      field: "customWhatsappNumber",
      headerName: "WhatsApp",
      sortable: true,
      filter: true,
      flex: 1.5,
      minWidth: 120,
    },
    {
      headerName: "Preview",
      field: "preview",
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      resizable: false,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      suppressMenu: true,
      cellRenderer: previewCellRenderer,
      cellStyle: { textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" },
    },
  ], [previewCellRenderer]);

  // Grid defaults
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    minWidth: 100,
    cellStyle: { fontSize: '0.75rem' },
    headerClass: 'small-header',
  }), []);

  // Grid ready handler
  const onGridReady = useCallback((params) => {
    gridRef.current = params.api;
    params.api.sizeColumnsToFit({ defaultMinWidth: 100 });
  }, []);

  const renderedMessage = useMemo(() => {
    if (!selectedTemplate?.content) return "";
    let content = selectedTemplate.content;
    const name = selectedPreviewStudent?.fullName || "Student";
    const today = new Date().toLocaleDateString();
    // Fill built-ins
    content = content.replace(/\{name\}/gi, name);
    content = content.replace(/\{date\}/gi, paramValues.date || today);
    // Fill any other params
    placeholders.forEach((key) => {
      if (key === "name" || key === "date") return;
      const val = paramValues[key] ?? "";
      const re = new RegExp(`\\{${key}\\}`, "gi");
      content = content.replace(re, val);
    });
    return content;
  }, [selectedTemplate, selectedPreviewStudent, paramValues, placeholders]);

  const mapServerTemplate = (t) => ({
    id: t.templateId,
    name: t.templateName,
    content: t.templateContent,
    providerTemplateName: t.providerTemplateName,
    createdBy: t.createdBy,
    createdDate: t.createdDate,
    modifiedBy: t.modifiedBy,
    modifiedDate: t.modifiedDate,
    isActive: t.isActive,
  });

  // Fetch alumnus data with registration type
  const fetchAlumnusData = useCallback(async (regType = registrationType) => {
    try {
      setLoading(true);
      const response = await api.post(API_ENDPOINTS.ADMIN.GET_ALUMNUS_DETAIL_INVITATION, {
        registedType: regType
      });
      setRowData(response.data);
    } catch (error) {
      console.error("Error fetching alumnus data:", error);
      safeToast.error("Failed to fetch alumnus data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [registrationType]);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const res = await api.get("/AdminMaster/get-all-templates");
      const items = Array.isArray(res.data) ? res.data.map(mapServerTemplate) : [];
      setSmsTemplates(items);
    } catch (err) {
      console.error("Failed to load templates", err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    fetchAlumnusData();
    fetchTemplates();
  }, [fetchAlumnusData, fetchTemplates]);

  // Export CSV function
  const exportCsv = useCallback(() => {
    if (!rowData || rowData.length === 0) {
      safeToast.warn('No data to export');
      return;
    }

    try {
      // Manual CSV export since AG Grid export might not be working
      const headers = ['S.No', 'Name', 'WhatsApp'];
      const csvContent = [
        headers.join(','),
        ...rowData.map((row, index) => [
          index + 1,
          `"${(row.fullName || '').replace(/"/g, '""')}"`,
          `"${(row.customWhatsappNumber || '').replace(/"/g, '""')}"`
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `alumni-details-${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export error:', error);
      safeToast.error('Failed to export CSV. Please try again.');
    }
  }, [rowData]);

  const handleAddTemplate = useCallback(() => {
    setEditingTemplate(null);
    setNewTemplateName("");
    setNewTemplateContent("");
    setTemplateDialogOpen(true);
  }, []);

  const handleEditTemplate = useCallback((template) => {
    setEditingTemplate(template);
    setNewTemplateName(template.name);
    setNewTemplateContent(template.content);
    setTemplateDialogOpen(true);
  }, []);

  // Delete option removed per requirement

  const handleSaveTemplate = useCallback(async () => {
    if (!newTemplateName.trim() || !newTemplateContent.trim()) return;
    try {
      const payload = {
        templateId: editingTemplate ? editingTemplate.id : 0,
        templateName: newTemplateName.trim(),
        templateContent: newTemplateContent.trim(),
        user: "1", // TODO: replace with actual logged-in user id if available
      };
      const res = await api.post("/AdminMaster/merge-template", payload);
      if (res.data?.result) {
        await fetchTemplates();
        setTemplateDialogOpen(false);
        setEditingTemplate(null);
      }
    } catch (err) {
      console.error("Failed to save template", err);
      safeToast.error("Failed to save template. Please try again.");
    }
  }, [editingTemplate, newTemplateName, newTemplateContent, fetchTemplates]);

  const handleStudentSelection = useCallback((student, checked) => {
    setSelectedStudents(prev => {
      if (checked) {
        // Add student if not already selected
        return prev.some(s => s.id === student.id) ? prev : [...prev, student];
      } else {
        // Remove student from selection
        return prev.filter(s => s.id !== student.id);
      }
    });
  }, []);

  // Remove AG Grid selection handler - we'll use only custom checkbox logic

  const handleSendSms = useCallback(async () => {
    if (!selectedTemplate || selectedStudents.length === 0) return;

    setSendingSms(true);
    try {
      // Separate students by type (UploadId vs StudentId)
      const uploadIds = [];
      const studentIds = [];
      
      selectedStudents.forEach(student => {
        // Check if student has uploadId (from CurrentStudentsUpload) or studentId (from StudentInfo)
        // The API returns StudentId (PascalCase) which becomes studentId (camelCase) in JSON
        if (student.uploadId) {
          uploadIds.push(student.uploadId);
        } else if (student.studentId) {
          studentIds.push(student.studentId);
        } else if (student.id) {
          // Fallback to id if studentId is not available
          studentIds.push(student.id);
        }
      });

      // Parameters are now set in the backend, no need to send from frontend
      // Prepare request payload
      const payload = {
        UploadIds: uploadIds,
        StudentIds: studentIds,
        TemplateId: selectedTemplate.id,
        ProviderTemplateName: selectedTemplate.providerTemplateName || selectedTemplate.name,
        SentBy: 1, // TODO: Replace with actual logged-in user ID
        Parameters: null // Parameters are set in backend
      };

      console.log('Sending WhatsApp invitations:', payload);

      // Call WhatsApp API
      const response = await api.post(API_ENDPOINTS.WHATSAPP.SEND_INVITATIONS, payload);
      
      if (response.data?.success) {
        const { totalSent = 0, totalFailed = 0 } = response.data;
        if (totalSent > 0) {
          safeToast.success(`WhatsApp invitations sent successfully to ${totalSent} ${totalSent === 1 ? 'student' : 'students'}!${totalFailed > 0 ? ` ${totalFailed} failed.` : ''}`);
        } else {
          safeToast.warn(`No messages were sent. ${totalFailed > 0 ? `${totalFailed} failed.` : ''}`);
        }
        setSelectedStudents([]);
        setSelectedTemplate(null);
        setParamValues({});
      } else {
        throw new Error(response.data?.message || 'Failed to send invitations');
      }
    } catch (error) {
      console.error('WhatsApp sending error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send WhatsApp invitations. Please try again.';
      safeToast.error(errorMessage);
    } finally {
      setSendingSms(false);
    }
  }, [selectedTemplate, selectedStudents, paramValues, placeholders, allPlaceholdersInOrder]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "350px 1fr" }, gap: 2, p: 2, height: "88vh", boxSizing: "border-box" }}>
      {/* Left: SMS Templates */}
      <Paper variant="outlined" sx={{ p: 1.5, overflow: "hidden", display: "flex", flexDirection: "column", gap: 2, height: 240 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>SMS Templates</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAddTemplate}>
            Add
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 160, overflow: "auto" }}>
          {loadingTemplates ? (
            <Typography variant="caption" color="text.secondary">Loading templates...</Typography>
          ) : smsTemplates.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No templates found.</Typography>
          ) : smsTemplates.map((template) => (
            <Paper
              key={template.id}
              variant="outlined"
              sx={{
                p: 1,
                cursor: 'pointer',
                bgcolor: selectedTemplate?.id === template.id ? 'success.light' : 'background.paper',
                border: selectedTemplate?.id === template.id ? 2 : 1,
                borderColor: selectedTemplate?.id === template.id ? 'success.main' : 'divider',
                '&:hover': {
                  bgcolor: selectedTemplate?.id === template.id ? 'success.light' : 'action.hover'
                }
              }}
              onClick={() => setSelectedTemplate(template)}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block' }}>
                    {template.name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                    {template.content.length > 50 ? template.content.substring(0, 50) + '...' : template.content}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditTemplate(template); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </Paper>

        {/* Right: Student Selection */}
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, height: "100%", gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, height: 240, display: "flex", flexDirection: "column", gap: 1, overflow: "hidden" }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Alumni Details</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                    {rowData.length} records found
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Autocomplete
                    size="small"
                    sx={{ minWidth: 150 }}
                    options={[
                      { value: 0, label: 'All Alumni' },
                      { value: 1, label: 'Registered Users' },
                      { value: 2, label: 'Unregistered Alumni' }
                    ]}
                    value={{ value: registrationType, label: registrationType === 0 ? 'All Alumni' : registrationType === 1 ? 'Registered Users' : 'Unregistered Alumni' }}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setRegistrationType(newValue.value);
                        fetchAlumnusData(newValue.value);
                      }
                    }}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Registration Type"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                          '& .MuiInputBase-input': { fontSize: '0.7rem' }
                        }}
                      />
                    )}
                  />
                  <Button                   
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={exportCsv}
                    disabled={rowData.length === 0}
                    sx={{ fontSize: '0.7rem' }}
                  >
                    Export CSV
                  </Button>
                </Box>
              </Box>
              <Button
                variant="contained"
                color="success"
                startIcon={<SendIcon />}
                onClick={handleSendSms}
                disabled={!selectedTemplate || selectedStudents.length === 0 || sendingSms}
              >
                {sendingSms ? 'Sending...' : `Send SMS (${selectedStudents.length})`}
              </Button>
            </Box>

          <div className="ag-theme-alpine" style={{ flex: 1, minHeight: 0, width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              animateRows={true}
              suppressRowClickSelection={true}
              rowHeight={30}
              headerHeight={35}
              pagination={true}
              paginationPageSize={18}            
              suppressMenuHide={true}
            />
          </div>
        </Paper>

        {/* Message Preview and Parameters */}
        <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, height: 300, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Message Preview</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              {selectedTemplate ? selectedTemplate.name : 'Select a template'}
            </Typography>
          </Box>

          {/* Parameter inputs */}
          {selectedTemplate ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {placeholders.filter(k => k !== 'name').map((key) => (
                <TextField
                  key={key}
                  label={key.toUpperCase()}
                  size="small"
                  value={paramValues[key] ?? ''}
                  onChange={(e) => setParamValues(prev => ({ ...prev, [key]: e.target.value }))}
                  sx={{ minWidth: 120, flex: '1 1 140px' }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">Choose a template to configure parameters.</Typography>
          )}

          {/* Selected student */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Student:</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {selectedPreviewStudent?.fullName || 'Click Preview on a student'}
            </Typography>
          </Box>

          {/* Rendered message */}
          <Box sx={{ flex: 1, minHeight: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, overflow: 'auto', bgcolor: 'background.paper' }}>
            <Typography sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {renderedMessage || 'Preview will appear here when a template and student are selected.'}
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Template Add/Edit Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Template' : 'Add Template'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template Name"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              size="small"
            />
            <TextField
              label="Template Content"
              value={newTemplateContent}
              onChange={(e) => setNewTemplateContent(e.target.value)}
              multiline
              minRows={4}
            />
            <Typography variant="caption" color="text.secondary">
              You can use variables like {`{name}`} and {`{date}`} which will be replaced automatically.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button  onClick={handleSaveTemplate}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

SmsInvitationManager.propTypes = {
  // No props needed - component fetches its own data
};

export default SmsInvitationManager;
